import { useEffect, useRef, useState } from "react";
import { loadWorkspace, saveWorkspace, type Workspace, type WorkspaceUpdate } from "./workspace";

function changed(a: Workspace | null, b: Workspace | null) {
  return a?.source !== b?.source || a?.reference !== b?.reference || a?.events !== b?.events || a?.history !== b?.history;
}

/** Serialize automatic and manual saves against one durable compare-and-swap revision. */
export function useLocalWorkspace() {
  const [workspace, setValue] = useState<Workspace | null>(null);
  const [ready, setReady] = useState(false);
  const [canSave, setCanSave] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");
  const current = useRef<Workspace | null>(null);
  const committed = useRef<Workspace | null>(null);
  const revision = useRef<number | null>(null);
  const alive = useRef(true);
  const busy = useRef(false);
  const queued = useRef(false);
  const autoBlocked = useRef(false);

  const update: WorkspaceUpdate = value => {
    const next = typeof value === "function" ? current.current ? value(current.current) : null : value;
    current.current = next; setValue(next);
  };
  useEffect(() => {
    alive.current = true;
    loadWorkspace().then(saved => {
      if (!alive.current) return;
      const restored = saved?.workspace ?? null;
      current.current = restored; committed.current = restored; revision.current = saved?.revision ?? 0;
      setValue(restored); setCanSave(true);
      setStatus(saved ? "Restored your locally saved workspace. Replay is paused." : "Choose training or import a recording to get started.");
    }).catch(cause => {
      if (alive.current) setLoadError(cause instanceof Error ? cause.message : "Could not restore the local workspace.");
    }).finally(() => { if (alive.current) setReady(true); });
    const beforeUnload = (event: BeforeUnloadEvent) => {
      if (busy.current || changed(current.current, committed.current)) { event.preventDefault(); event.returnValue = ""; }
    };
    window.addEventListener("beforeunload", beforeUnload);
    return () => { alive.current = false; window.removeEventListener("beforeunload", beforeUnload); };
  }, []);

  async function save(manual = false) {
    if (!alive.current || !current.current || revision.current === null || (!manual && autoBlocked.current)) return;
    if (manual) autoBlocked.current = false;
    if (busy.current) { queued.current = true; return; }
    busy.current = true; queued.current = false; setSaving(true); setSaveError(null);
    const snapshot = current.current;
    try {
      const nextRevision = await saveWorkspace(snapshot, revision.current);
      revision.current = nextRevision; committed.current = snapshot;
      if (alive.current) setStatus(changed(current.current, snapshot) ? "Saving newer changes…" : manual ? "Workspace snapshot saved on this browser." : "Changes saved automatically on this browser.");
    } catch (cause) {
      autoBlocked.current = true;
      const message = cause instanceof Error ? cause.message : "Local save failed. Export your workspace to preserve it.";
      if (alive.current) { setSaveError(message); setStatus("Automatic saving paused. Export your work before reloading."); }
    } finally {
      busy.current = false;
      if (alive.current) {
        setSaving(false);
        if (!autoBlocked.current && (queued.current || changed(current.current, snapshot))) void save();
      }
    }
  }
  useEffect(() => {
    if (!ready || !canSave || !workspace || !changed(workspace, committed.current)) return;
    const timer = setTimeout(() => void save(), 500);
    return () => clearTimeout(timer);
    // Replay motion is transient. Save metadata changes automatically and position on manual save.
  }, [ready, canSave, workspace?.source, workspace?.reference, workspace?.events, workspace?.history]);

  return {hasUnsavedChanges: () => busy.current || changed(current.current, committed.current), workspace, update, ready, canSave, loadError, saveError, saving, status, setStatus, save: () => save(true)};
}
